import { Text } from '@/ui-kit'
import clsx from 'clsx'
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react'
import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useUiKit } from '../providers'
import styles from './Tabs.module.scss'

function usePrevious<T>(value: T): T {
  const ref = useRef<T>(value)

  useEffect(() => {
    ref.current = value
  })

  return ref.current
}

type TitleValue = {
  title: string | ReactNode
  value: string
  wip?: boolean
}

interface TabsProps<T extends TitleValue> {
  tabs: T[]
  value: T['value'] | null
  onChange?: (tab: T) => void
  size?: 'default' | 'small'
  orientation?: 'horizontal' | 'vertical'
  className?: string
}

export function Tabs<T extends TitleValue>({
  tabs,
  value,
  onChange,
  size = 'default',
  orientation = 'horizontal',
  className
}: TabsProps<T>): React.JSX.Element {
  const ulRef = useRef<HTMLUListElement>(null)
  const [tabsWidths, setTabsWidths] = useState<number[]>([])
  const tabIndex = useMemo(() => tabs.findIndex((tab) => tab.value === value), [tabs, value])
  const prevTabIndex = usePrevious(tabIndex)
  const { isMobile, isThemeDark } = useUiKit()

  const tabsKey = tabs.map((tab) => `${tab.value}:${typeof tab.title === 'string' ? tab.title : ''}`).join('|')

  useLayoutEffect(() => {
    const list = ulRef.current
    if (!list) return

    const measure = (): void =>
      setTabsWidths(Array.from(list.children).map((tab) => (tab as HTMLElement).offsetWidth))

    measure()

    const observer = new ResizeObserver(measure)
    Array.from(list.children).forEach((child) => observer.observe(child))

    return (): void => observer.disconnect()
  }, [tabsKey])

  return (
    <div className={clsx('relative overflow-hidden rounded-xl', className)}>
      {orientation === 'horizontal' && (
        <svg
          className={clsx('absolute top-0 left-0', size === 'default' ? 'h-14' : 'h-10')}
          width="100%"
          viewBox={size === 'default' ? '0 0 476 56' : '0 0 283 40'}
          preserveAspectRatio="none"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.5 19.2C0.5 15.8314 0.500389 13.3351 0.66183 11.3591C0.822794 9.38904 1.14136 7.98035 1.75343 6.77911C2.85597 4.61525 4.61525 2.85597 6.77911 1.75343C7.98035 1.14136 9.38904 0.822794 11.3591 0.66183C13.3351 0.500389 15.8314 0.5 19.2 0.5H263.8C267.169 0.5 269.665 0.500389 271.641 0.66183C273.611 0.822794 275.02 1.14136 276.221 1.75342C278.385 2.85597 280.144 4.61524 281.247 6.77911C281.859 7.98035 282.177 9.38904 282.338 11.3591C282.5 13.3351 282.5 15.8314 282.5 19.2V20.8C282.5 24.1686 282.5 26.6649 282.338 28.6409C282.177 30.611 281.859 32.0197 281.247 33.2209C280.144 35.3848 278.385 37.144 276.221 38.2466C275.02 38.8586 273.611 39.1772 271.641 39.3382C269.665 39.4996 267.169 39.5 263.8 39.5H19.2C15.8314 39.5 13.3351 39.4996 11.3591 39.3382C9.38904 39.1772 7.98035 38.8586 6.77911 38.2466C4.61525 37.144 2.85597 35.3848 1.75342 33.2209C1.14136 32.0197 0.822794 30.611 0.66183 28.6409C0.500389 26.6649 0.5 24.1686 0.5 20.8V19.2Z"
            fill="#FF9735"
            fillOpacity="0.06"
          />
        </svg>
      )}
      <OverlayScrollbarsComponent
        options={{
          overflow: {
            x: isMobile ? (orientation === 'vertical' ? 'hidden' : 'scroll') : 'hidden',
            y: 'hidden'
          },
          scrollbars: {
            autoHide: 'leave',
            autoHideDelay: 500,
            dragScroll: true,
            theme: isThemeDark ? 'os-theme-light' : 'os-theme-dark'
          }
        }}
        defer
      >
        <div
          className={clsx(styles.Tabs, {
            [styles.active]: value !== null,
            [styles.small]: size === 'small',
            [styles.mobile]: isMobile,
            [styles.positionAnimation]: !!value && prevTabIndex > -1 && prevTabIndex !== tabIndex
          })}
        >
          <ul ref={ulRef} className={clsx(styles.List, orientation === 'vertical' && 'flex-col')}>
            {tabs.map((tab, index) => (
              <li
                key={tab.value}
                className={clsx(styles.Tab, orientation === 'vertical' && !isMobile && '!text-left', {
                  [styles.active]: tab.value === value
                })}
                onClick={() => !tab.wip && onChange?.(tab)}
              >
                <Text
                  className={clsx(
                    'whitespace-nowrap',
                    tab.wip
                      ? 'text-gray-90 cursor-not-allowed'
                      : tabIndex === index
                        ? 'colorWhite'
                        : 'colorWhiteToBlack'
                  )}
                  type="body1-semibold"
                >
                  {tab.title}
                </Text>
              </li>
            ))}
          </ul>
          {!!tabsWidths.length && (
            <div
              className={clsx(orientation === 'horizontal' ? 'h-8' : 'h-12', styles.Cursor, {
                [styles.leftAnimation]: !!value && prevTabIndex > -1
              })}
              style={{
                width: orientation === 'horizontal' ? tabsWidths[tabIndex] : '100%',
                left:
                  orientation === 'horizontal'
                    ? `calc(4px + ${tabsWidths.reduce((result, width, index) => (index < tabIndex ? result + width - 0.5 : result), 0)}px)`
                    : 'auto',
                top: orientation === 'vertical' ? `calc(4px + ${tabIndex * 48}px)` : '4px'
              }}
            />
          )}
        </div>
      </OverlayScrollbarsComponent>
      {orientation === 'horizontal' && (
        <svg
          className={clsx('pointer-events-none absolute top-0 left-0', size === 'default' ? 'h-14' : 'h-10')}
          width="100%"
          viewBox={size === 'default' ? '0 0 476 56' : '0 0 283 40'}
          preserveAspectRatio="none"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0.5 19.2C0.5 15.8314 0.500389 13.3351 0.66183 11.3591C0.822794 9.38904 1.14136 7.98035 1.75343 6.77911C2.85597 4.61525 4.61525 2.85597 6.77911 1.75343C7.98035 1.14136 9.38904 0.822794 11.3591 0.66183C13.3351 0.500389 15.8314 0.5 19.2 0.5H263.8C267.169 0.5 269.665 0.500389 271.641 0.66183C273.611 0.822794 275.02 1.14136 276.221 1.75342C278.385 2.85597 280.144 4.61524 281.247 6.77911C281.859 7.98035 282.177 9.38904 282.338 11.3591C282.5 13.3351 282.5 15.8314 282.5 19.2V20.8C282.5 24.1686 282.5 26.6649 282.338 28.6409C282.177 30.611 281.859 32.0197 281.247 33.2209C280.144 35.3848 278.385 37.144 276.221 38.2466C275.02 38.8586 273.611 39.1772 271.641 39.3382C269.665 39.4996 267.169 39.5 263.8 39.5H19.2C15.8314 39.5 13.3351 39.4996 11.3591 39.3382C9.38904 39.1772 7.98035 38.8586 6.77911 38.2466C4.61525 37.144 2.85597 35.3848 1.75342 33.2209C1.14136 32.0197 0.822794 30.611 0.66183 28.6409C0.500389 26.6649 0.5 24.1686 0.5 20.8V19.2Z"
            stroke="url(#paint0_linear_1062_1105)"
          />
          <defs>
            <linearGradient
              id="paint0_linear_1062_1105"
              x1="-80.5155"
              y1="-6.66666"
              x2="140.258"
              y2="174.36"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#FFC792" stopOpacity="0" />
              <stop offset="1" stopColor={isThemeDark ? '#040404' : '#f6f6f6'} />
            </linearGradient>
          </defs>
        </svg>
      )}
    </div>
  )
}
